import { computed, ref } from 'vue';
import axios from 'axios';
import { store } from '../store';
import { isImpersonating } from './session';
import { t } from '../i18n';
import { ROLES } from './matchmakingSounds';

const state = ref({});

// Applied locally, not yet answered for. Layered back over every response: a
// request already in flight answers with a payload that predates them.
let unacknowledged = {};

export const preferences = computed(() => state.value);

// True until the first load settles, so callers can tell "not set" apart from
// "not fetched yet".
export const preferencesLoading = ref(true);

let loaded = false;

// `preferencesLoading` says the load settled, which a failure also does; this
// says it settled with a payload. A caller writing back a value it chose
// *because* nothing was stored must check this one -- otherwise a failed load
// makes every default look deliberate and overwrites what it could not read.
export const preferencesLoaded = ref(false);

function adopt({ data }) {
  state.value = { ...(data.preferences ?? {}), ...unacknowledged };
  loaded = true;
  preferencesLoaded.value = true;
}

// A key changed again since is left alone: it is waiting for its own response.
function acknowledge(changes) {
  unacknowledged = Object.fromEntries(
    Object.entries(unacknowledged).filter(([key, value]) => (
      !(key in changes) || changes[key] !== value
    )),
  );
}

function fetchPreferences() {
  return axios.get('/user_preferences').then(adopt);
}

function putPreferences(changes) {
  // These are the impersonated user's preferences: an admin opening Play Now
  // picks a mod and a map to get there at all, and every pick would land on
  // their account. The change still applies locally, it just never follows them.
  if (isImpersonating()) return Promise.resolve();

  return axios.put('/user_preferences', { preferences: changes }).then(adopt);
}

let pendingRequest = Promise.resolve();

// Only writes report: the user just did something and it was not kept. A failed
// load is nobody's action -- the page shows defaults and the next write reports
// for itself -- so it stays quiet.
function enqueue(request, failureMessage) {
  pendingRequest = pendingRequest.then(request).catch((error) => {
    console.error('Error syncing user preferences:', error);

    if (failureMessage) {
      store.commit('showMessage', { messageType: 'error', messageText: failureMessage });
    }
  });

  return pendingRequest;
}

const writeFailed = () => t('shared.user_preferences.save_failed');

function stage(changes) {
  unacknowledged = { ...unacknowledged, ...changes };
  state.value = { ...state.value, ...changes };
}

// Acknowledged on failure too: holding the key would pin a value the server
// never took, re-layering it over every later response and masking a newer one
// from another device -- and it is lost on reload regardless.
function applyPreferences(changes) {
  stage(changes);

  return enqueue(() => putPreferences(changes).finally(() => acknowledge(changes)), writeFailed());
}

// An object is a plain overwrite. Pass a function to derive the changes from
// what the server last returned -- the favourite mods need it, since the list
// goes up whole and deriving it from the empty stand-in would truncate it.
export function updatePreferences(update) {
  if (typeof update !== 'function') return applyPreferences(update);

  // Nothing to derive from yet: fetch first, and let the write fall away with a
  // failed load rather than wipe what the failure could not read.
  if (!loaded) {
    return enqueue(() => fetchPreferences().then(() => {
      const changes = update(state.value);

      // The fetched payload already says what the click asked for: a retry.
      if (Object.keys(changes).length === 0) return undefined;

      stage(changes);

      return putPreferences(changes).finally(() => acknowledge(changes));
    }), writeFailed());
  }

  // Derived here rather than inside the queued job, so the change lands on the
  // click instead of waiting for everything ahead of it in the queue.
  const changes = update(state.value);

  if (Object.keys(changes).length === 0) return Promise.resolve();

  stage(changes);

  return enqueue(() => (
    // Sent as `state` stands when the request goes out, not as the snapshot
    // above. A write queued behind another was built before that one ran, so its
    // own snapshot would put back a list from before -- two quick stars, and the
    // first is dropped.
    putPreferences(Object.fromEntries(
      Object.keys(changes).map((key) => [key, state.value[key]]),
    )).finally(() => acknowledge(changes))
  ), writeFailed());
}

// Play Now renders its panels on an exact match, so an unknown tab renders
// none. Validated on the way out too: a browser may have migrated a bad one
// before this guard existed.
export const PLAY_NOW_TABS = ['coach', 'favorites', 'browse'];

function validTab(tab) {
  return PLAY_NOW_TABS.includes(tab) ? tab : null;
}

function readLegacyJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (error) {
    console.error(`Error reading legacy ${key}:`, error);

    return null;
  }
}

// Matchmaking sounds muted in this browser before they lived on the account, by role. Carried
// over where the account holds no mute for the role, and the key goes once the write lands; a
// failed write leaves it for the next load. Sign-out sweeps it, so it never reaches another user.
const LEGACY_MUTED_KEY = 'matchmakingMutedSounds';

function bootstrapLegacyMutes() {
  if (isImpersonating()) return Promise.resolve();

  const muted = readLegacyJson(LEGACY_MUTED_KEY);

  if (!Array.isArray(muted) || muted.length === 0) return Promise.resolve();

  const settings = state.value.audio_settings ?? {};
  const sounds = { ...settings.sounds };

  muted.forEach((role) => {
    // Known sounds only: one the server refuses would sink every later audio write with it
    if (Object.hasOwn(ROLES, role) && sounds[role]?.muted === undefined) {
      sounds[role] = { ...sounds[role], muted: true };
    }
  });

  const forget = () => {
    try {
      localStorage.removeItem(LEGACY_MUTED_KEY);
    } catch (error) {
      console.error(`Error removing legacy ${LEGACY_MUTED_KEY}:`, error);
    }
  };
  const changes = { audio_settings: { ...settings, sounds } };

  if (JSON.stringify(changes.audio_settings) === JSON.stringify(settings)) {
    forget();

    return Promise.resolve();
  }

  stage(changes);

  return putPreferences(changes).finally(() => acknowledge(changes)).then(forget);
}

let loadRequest = null;

// Loads once per session, so it can be mounted at the layout without refetching.
export function loadUserPreferences() {
  if (!loadRequest) {
    // A retry is a load too. Left false, a component mounting during it reads
    // "settled" off a payload that never arrived and takes a default.
    preferencesLoading.value = true;

    loadRequest = enqueue(() => fetchPreferences().then(bootstrapLegacyMutes)).then(() => {
      preferencesLoading.value = false;

      // A failed load is not a load: drop it so the next caller fetches again.
      if (!loaded) loadRequest = null;
    });
  }

  return loadRequest;
}

export const favoriteModIds = computed(() => preferences.value.play_now_favorite_mod_ids ?? []);

export function isFavoriteMod(modId) {
  return favoriteModIds.value.includes(modId);
}

// Sends the state the click asked for, not a blind flip of the list as it
// stands when the request goes out. Before the first response the star does not
// fill, so a second click is a retry rather than a change of mind -- a flip
// would take the favourite straight back off.
export function toggleFavoriteMod(modId) {
  const wanted = !isFavoriteMod(modId);

  return updatePreferences((current) => {
    const modIds = current.play_now_favorite_mod_ids ?? [];

    if (modIds.includes(modId) === wanted) return {};

    return {
      play_now_favorite_mod_ids: wanted
        ? [...modIds, modId]
        : modIds.filter((id) => id !== modId),
    };
  });
}

export const serverLocationId = computed(() => preferences.value.server_location_id ?? null);

// The stored preference is only an id, so it is resolved against the caller's
// list, falling back to the first entry.
//
// Nothing until the id is readable: resolving mid-load returns that fallback as
// if it were the stored choice, which puts a location on screen that a launch
// then starts a server in.
export function resolveServerLocation(locations) {
  if (preferencesLoading.value || !Array.isArray(locations)) return null;

  const stored = locations.find((location) => location.id === serverLocationId.value);

  return stored ?? locations[0] ?? null;
}

export function setServerLocation(location) {
  return updatePreferences({ server_location_id: location?.id ?? null });
}

export const playNowActiveTab = computed(() => validTab(preferences.value.play_now_active_tab));
export const playNowModId = computed(() => preferences.value.play_now_mod_id ?? null);
export const playNowMapId = computed(() => preferences.value.play_now_cs_map_id ?? null);

export function setPlayNowActiveTab(tab) {
  return updatePreferences({ play_now_active_tab: tab });
}

export function setPlayNowMod(mod) {
  return updatePreferences({ play_now_mod_id: mod?.id ?? null });
}

export function setPlayNowMap(map) {
  return updatePreferences({ play_now_cs_map_id: map?.id ?? null });
}

export const matchmakingPlayModeId = computed(() => (
  preferences.value.matchmaking_play_mode_id ?? null
));
export const matchmakingRegionId = computed(() => preferences.value.matchmaking_region_id ?? null);
export const matchmakingCsMapNames = computed(() => (
  preferences.value.matchmaking_cs_map_names ?? []
));

export function setMatchmakingPlayMode(playMode, mapNames) {
  return updatePreferences({
    matchmaking_play_mode_id: playMode?.id ?? null,
    matchmaking_cs_map_names: mapNames ?? [],
  });
}

export function setMatchmakingRegion(region) {
  return updatePreferences({ matchmaking_region_id: region?.id ?? null });
}

export function setMatchmakingMapNames(mapNames) {
  return updatePreferences({ matchmaking_cs_map_names: mapNames ?? [] });
}

// Settings > Audio, by matchmaking sound: { sounds: { <role>: { sound, volume, muted } } }, with
// anything left out at its default (see resolveAudioSettings in matchmakingSounds.js).
// The same object while none is stored, so a watcher does not fire on other preferences' writes
const NO_AUDIO_SETTINGS = Object.freeze({});
export const audioSettings = computed(() => preferences.value.audio_settings ?? NO_AUDIO_SETTINGS);

// Derived from the latest state, so two quick changes to different sounds both stand.
// changes by role, saved in one write
export function setSoundsSettings(byRole) {
  return updatePreferences((current) => {
    const settings = current.audio_settings ?? {};
    const sounds = { ...settings.sounds };
    Object.entries(byRole).forEach(([role, changes]) => {
      sounds[role] = { ...sounds[role], ...changes };
    });

    return { audio_settings: { ...settings, sounds } };
  });
}

export function setSoundSettings(role, changes) {
  return setSoundsSettings({ [role]: changes });
}

export function resetAudioSettings() {
  return updatePreferences({ audio_settings: null });
}
